import React, { useState, useEffect } from 'react';
import { Plus, Trash2, List, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { useConfirm } from '@/hooks/useConfirm';

const SchedulesConfig = () => {
  const [schedules, setSchedules] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ location_id: '', day_of_week: 0, start_time: '09:00', end_time: '18:00' });
  const [viewMode, setViewMode] = useState('list');
  const { confirm, ConfirmDialog } = useConfirm();

  const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [schRes, locsRes] = await Promise.all([api.getSchedules(), api.getLocations()]);
      setSchedules(schRes.data);
      setLocations(locsRes.data);
    } catch (error) {
      toast.error('Error al cargar horarios');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos obligatorios
    if (!formData.location_id || formData.day_of_week === undefined || !formData.start_time || !formData.end_time) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }
    
    try {
      await api.createSchedule(formData);
      toast.success('Horario creado');
      setShowModal(false);
      setFormData({ location_id: locations[0]?.id || '', day_of_week: 0, start_time: '09:00', end_time: '18:00' });
      loadData();
    } catch (error) {
      toast.error('Error al guardar horario');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Horarios de Atención</h2>
        <div className="flex space-x-2">
          <div className="flex border border-[#C9A961]/30 rounded">
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'bg-[#C9A961]' : ''}>
              <List size={16} />
            </Button>
            <Button variant={viewMode === 'cards' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('cards')} className={viewMode === 'cards' ? 'bg-[#C9A961]' : ''}>
              <LayoutGrid size={16} />
            </Button>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-[#C9A961]">
            <Plus className="mr-2" size={18} /> Nuevo
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="border border-[#C9A961]/20 rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Día</TableHead>
                <TableHead>Local</TableHead>
                <TableHead>Horario</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedules.map((sch) => {
                const loc = locations.find(l => l.id === sch.location_id);
                return (
                  <TableRow key={sch.id}>
                    <TableCell className="font-medium">{days[sch.day_of_week]}</TableCell>
                    <TableCell>{loc?.name || '-'}</TableCell>
                    <TableCell className="text-[#C9A961] font-semibold">{sch.start_time} - {sch.end_time}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="text-red-500" onClick={async () => {
                        const confirmed = await confirm('Eliminar Horario', '¿Estás seguro de eliminar este horario? Esta acción no se puede deshacer.');
                        if (confirmed) {
                          await api.deleteSchedule(sch.id);
                          loadData();
                        }
                      }}>
                        <Trash2 size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {schedules.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-[#6B6B6B] py-8">No hay horarios registrados</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="space-y-3">
          {schedules.map((sch) => {
            const loc = locations.find(l => l.id === sch.location_id);
            return (
              <Card key={sch.id} className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">{days[sch.day_of_week]}</h3>
                  <p className="text-sm text-[#6B6B6B]">{loc?.name || 'Local'}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <p className="font-medium text-[#C9A961]">{sch.start_time} - {sch.end_time}</p>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={async () => {
                    const confirmed = await confirm('Eliminar Horario', '¿Estás seguro de eliminar este horario? Esta acción no se puede deshacer.');
                    if (confirmed) {
                      await api.deleteSchedule(sch.id);
                      loadData();
                    }
                  }}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-cormorant text-[#C9A961]">Nuevo Horario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Local</Label>
              <Select value={formData.location_id} onValueChange={(v) => setFormData({...formData, location_id: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{locations.map(loc => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Día</Label>
              <Select value={formData.day_of_week.toString()} onValueChange={(v) => setFormData({...formData, day_of_week: parseInt(v)})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{days.map((day, i) => <SelectItem key={i} value={i.toString()}>{day}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Hora Inicio</Label><Input type="time" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value})} /></div>
            <div><Label>Hora Fin</Label><Input type="time" value={formData.end_time} onChange={(e) => setFormData({...formData, end_time: e.target.value})} /></div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#C9A961]">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default SchedulesConfig;